import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CalendarClock, AlertTriangle, X } from 'lucide-react';
import useTelaMobile from '../../../utils/useTelaMobile';
// ⚠️ O vocabulário dos status é COMPARTILHADO com a fila e a ficha do
//    equipamento — ver o cabeçalho do arquivo para o porquê de não ser cópia.
import { rotuloStatus } from '../chamados-rotulos';

// =====================================================================
// Chamados — CALENDÁRIO DOS TÉCNICOS (Visão Geral do hub).
//
// Responde "onde cada técnico vai estar", de acordo com os chamados agendados.
//
// ⚠️⚠️ A UNIDADE DA TELA É **dia → técnico → loja**, não "dia → chamado". Um
// técnico com 3 chamados na FER no mesmo dia aparece UMA vez na FER, com o
// número ao lado — porque a pergunta é *onde ele está*, e não *quanto trabalho
// ele tem* (essa já é respondida pela aba Chamados). Listar 3 linhas da FER
// encheria a célula e responderia outra coisa.
//
// ⚠️ O DETALHE NÃO DESFAZ O AGRUPAMENTO — ELE FICA A UM CLIQUE (Julia,
// 03/09/2026). Clicar em "FER ×3" abre uma pop-up com os três ASSUNTOS. Foi o
// pedido dela: a célula respondia "quantos" e não havia como saber "quais" sem
// ir à fila procurar chamado por chamado. A célula continua sendo uma linha por
// loja; quem quer o detalhe pede.
//
// ⚠️ O BLOCO "A AGENDAR" É PARTE DO CALENDÁRIO, não um extra. Chamado atribuído
// e sem data não aparece em dia nenhum — sem esse bloco, a tela diria "nada para
// fazer" havendo fila esperando. É a mentira por omissão que o calendário mais
// facilmente contaria.
//
// ⚠️ A cor por TÉCNICO é semântica (diz "quem"), não identidade do módulo — é a
// mesma exceção registrada em ARQUITETURA.md › Paleta dos módulos para os status.
// =====================================================================

const API = '/api/chamados';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = 'var(--ch-cor)';
const COR_CLARA = 'var(--ch-cor-clara)';

// Cores por técnico — atribuídas por POSIÇÃO na lista que a API devolve (ela vem
// ordenada por nome, então a cor de cada pessoa é estável entre carregamentos).
// ⚠️ Escolhidas para se distinguirem entre si E do rosa do módulo, que fica
// reservado ao cromo da tela.
const CORES_TECNICO = ['#0369a1', '#15803d', '#a16207', '#7c3aed', '#0f766e', '#b91c1c'];

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** 'AAAA-MM' do mês corrente, em hora local. */
function mesAtual() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Soma meses a uma chave 'AAAA-MM' sem passar por Date (evita fuso). */
function somarMes(chave, delta) {
    const [a, m] = chave.split('-').map(Number);
    const total = (a * 12) + (m - 1) + delta;
    const ano = Math.floor(total / 12);
    const mes = (total % 12) + 1;
    return `${ano}-${String(mes).padStart(2, '0')}`;
}

/**
 * As células do grid do mês: começa no domingo da semana do dia 1 e vai até
 * completar a última semana.
 * ⚠️ Construído com `new Date(ano, mes, dia)` (construtor LOCAL), nunca com
 * string — string 'AAAA-MM-DD' é lida como UTC e volta um dia no Brasil.
 */
function celulasDoMes(chave) {
    const [ano, mes] = chave.split('-').map(Number);
    const primeiro = new Date(ano, mes - 1, 1);
    const inicio = new Date(ano, mes - 1, 1 - primeiro.getDay());
    const celulas = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i);
        celulas.push({
            chave: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
            dia: d.getDate(),
            doMes: d.getMonth() === mes - 1,
        });
        // Para de completar quando já passou do mês e fechou a semana.
        if (i >= 27 && d.getMonth() !== mes - 1 && d.getDay() === 6) break;
    }
    return celulas;
}

/** 'AAAA-MM-DD' → 'dd/mm' (para o cabeçalho da pop-up). */
function diaCurto(chave) {
    const [, mm, dd] = String(chave).split('-');
    return `${dd}/${mm}`;
}

// Largura da pop-up. ⚠️ Fixa e não em `%`: ela é posicionada em `fixed` a partir
// da linha clicada, e é a largura conhecida que permite prendê-la dentro da tela.
const LARGURA_POPUP = 300;

/**
 * Onde desenhar a pop-up, a partir do retângulo da linha clicada.
 *
 * ⚠️⚠️ `position: fixed`, NUNCA `absolute` dentro da célula: a grade do mês vive
 * num contêiner com `overflow-x: auto` (é ele que permite o arrasto lateral em
 * tela estreita), e um filho absoluto seria RECORTADO por ele. A pop-up de uma
 * loja na coluna de sábado simplesmente não apareceria — e sem erro nenhum.
 *
 * ⚠️ Ela é PRESA à janela nos dois eixos: no horizontal para não vazar pela
 * direita nas últimas colunas; no vertical, virando para CIMA quando a linha
 * está no rodapé (as últimas semanas do mês), que é onde mais se clica.
 */
function posicaoDaPopup(rect) {
    const esquerda = Math.min(
        Math.max(8, rect.left),
        Math.max(8, window.innerWidth - LARGURA_POPUP - 8)
    );
    const espacoAbaixo = window.innerHeight - rect.bottom;
    // 240px é a altura típica da pop-up com 3 chamados; abaixo disso ela sobe.
    if (espacoAbaixo < 240 && rect.top > espacoAbaixo) {
        return { left: esquerda, bottom: window.innerHeight - rect.top + 6 };
    }
    return { left: esquerda, top: rect.bottom + 6 };
}

// ⚠️⚠️ O CALENDÁRIO SEGUE A FILA (`equipe`, 17/09/2026). O servidor assume 'TI'
//    quando ninguém passa `?equipe=`, então sem o parâmetro a agenda da
//    Manutenção mostraria os técnicos de TI — nomes reais, na loja certa, no dia
//    certo e do time errado. É o tipo de erro que só se descobre quando alguém
//    liga para o técnico que não vai àquela loja.
// ⚠️ Na prática são DOIS calendários (um por fila), sem duplicar tela: quem troca
//    a fila é a faixa do hub, e o mês escolhido acompanha.
const ChamadosCalendario = ({ equipe = 'TI' }) => {
    const [mes, setMes] = useState(mesAtual);
    const [agenda, setAgenda] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');

    // A pop-up aberta: qual dia, qual técnico, qual loja — e os chamados dela.
    // ⚠️ `null` = fechada. Guardar a POSIÇÃO junto (e não recalcular no render) é
    //    o que impede a pop-up de sair andando quando a página rola.
    const [detalhe, setDetalhe] = useState(null);

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const r = await fetch(
                `${API}/agenda?mes=${mes}&equipe=${encodeURIComponent(equipe)}`,
                { headers: auth() },
            );
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.message || 'Falha ao carregar a agenda.');
            }
            setAgenda(await r.json());
        } catch (e) {
            setErro(e.message);
            setAgenda(null);
        } finally {
            setCarregando(false);
        }
        // ⚠️ `equipe` na lista: trocar de fila tem de rebuscar a agenda. Sem ela, a
        //    faixa diria "Manutenção" e o calendário seguiria mostrando o time de TI.
    }, [mes, equipe]);

    useEffect(() => { carregar(); }, [carregar]);

    // Trocar de mês fecha a pop-up: os chamados dela são do mês que saiu da tela.
    useEffect(() => { setDetalhe(null); }, [mes]);

    /**
     * Abre (ou fecha) a pop-up de uma loja de um técnico num dia.
     *
     * ⚠️ Clicar de novo na MESMA linha fecha — é o gesto que a pessoa tenta antes
     *    de procurar o X, e sem isso o clique repetido só reposiciona a pop-up.
     */
    const alternarDetalhe = (evento, dia, linhaT, linhaL) => {
        const rect = evento.currentTarget.getBoundingClientRect();
        setDetalhe((atual) => {
            const mesma = atual
                && atual.dia === dia
                && atual.idTecnico === linhaT.idTecnico
                && atual.loja === linhaL.loja;
            if (mesma) return null;
            return {
                dia,
                idTecnico: linhaT.idTecnico,
                tecnico: linhaT.tecnico,
                loja: linhaL.loja,
                pos: posicaoDaPopup(rect),
                // ⚠️ `|| []` porque a pop-up é a única leitora do `itens`: agenda
                //    servida por um backend mais antigo (a janela de deploy em que
                //    o build novo conversa com o Node velho) não traria o campo, e
                //    o `.map` quebraria a tela inteira em vez de abrir vazia.
                itens: linhaL.itens || [],
            };
        });
    };

    // Esc fecha — atalho esperado de qualquer coisa que se sobrepõe à tela.
    useEffect(() => {
        if (!detalhe) return undefined;
        const aoTeclar = (e) => { if (e.key === 'Escape') setDetalhe(null); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [detalhe]);

    const corDoTecnico = (id) => {
        const i = (agenda?.tecnicos || []).findIndex((t) => t.id === id);
        return CORES_TECNICO[i >= 0 ? i % CORES_TECNICO.length : 0];
    };

    const hojeChave = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();

    const [ano, numMes] = mes.split('-').map(Number);
    // ⚠️ NO CELULAR A GRADE DO MÊS NÃO SERVE: 7 colunas em 360px dão células de
    //    ~48px, onde não cabe nome de loja nenhum. Lá o calendário vira LISTA DE
    //    AGENDA — só os dias que têm visita, em ordem. É outra leitura do mesmo
    //    dado: a grade responde "como está o mês", a lista responde "o que vem
    //    agora", que é a pergunta de quem está com o aparelho na mão.
    const { ehMobile } = useTelaMobile();

    const celulas = celulasDoMes(mes);

    return (
        <div style={{
            backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
            border: `1px solid ${COR_CLARA}`, marginBottom: '32px',
        }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <div>
                    <p style={{
                        fontSize: '12px', fontWeight: 800, textTransform: 'uppercase',
                        color: COR, margin: 0, letterSpacing: '0.04em',
                    }}>
                        {/* ⚠️ O sobretítulo NOMEIA A FILA: quem abre a Visão Geral
                            direto no calendário não passou pela faixa nem pelo
                            título, e "Agenda dos técnicos" sozinho não diz de qual
                            time são os nomes que ele está lendo. */}
                        Agenda dos técnicos · {equipe === 'Manutencao' ? 'Manutenção' : 'T.I'}
                    </p>
                    <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '4px 0 0 0' }}>
                        {MESES[numMes - 1]} de {ano}
                    </h3>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button type="button" aria-label="Mês anterior" onClick={() => setMes((m) => somarMes(m, -1))}
                        style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#475569', display: 'flex' }}>
                        <ChevronLeft size={16} />
                    </button>
                    <button type="button" onClick={() => setMes(mesAtual())}
                        style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                        Hoje
                    </button>
                    <button type="button" aria-label="Próximo mês" onClick={() => setMes((m) => somarMes(m, 1))}
                        style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#475569', display: 'flex' }}>
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            {/* Legenda — quem é cada cor. */}
            {!!agenda?.tecnicos?.length && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 16 }}>
                    {agenda.tecnicos.map((t) => (
                        <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: corDoTecnico(t.id), flexShrink: 0 }} />
                            {t.nome}
                        </span>
                    ))}
                </div>
            )}

            {erro && <p style={{ fontSize: 13, color: '#991b1b', margin: '0 0 12px 0' }}>{erro}</p>}
            {carregando && <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Carregando…</p>}

            {!carregando && agenda && (
                <>
                    {ehMobile ? (
                    <div>
                        {(() => {
                            // Só os dias COM visita, do mês em exibição, em ordem.
                            const comVisita = celulas
                                .filter((cel) => cel.doMes && (agenda.dias?.[cel.chave] || []).length > 0);
                            if (!comVisita.length) {
                                // ⚠️ Frase, não lista vazia: "nenhuma visita" é a resposta
                                //    à pergunta, e some se a tela só ficar em branco.
                                return (
                                    <p style={{ fontSize: 13, color: '#94a3b8', margin: 0, padding: '12px 0' }}>
                                        Nenhuma visita agendada neste mês.
                                    </p>
                                );
                            }
                            return comVisita.map((cel) => {
                                const linhas = agenda.dias[cel.chave];
                                const ehHoje = cel.chave === hojeChave;
                                const [, , dd] = cel.chave.split('-');
                                const diaSemana = DIAS_SEMANA[new Date(`${cel.chave}T12:00:00`).getDay()];
                                return (
                                    <div key={cel.chave} style={{
                                        display: 'flex', gap: 10, padding: '10px 0',
                                        borderBottom: '1px solid #f1f5f9',
                                    }}>
                                        {/* Coluna da data, estreita e de largura fixa: é o que
                                            deixa as visitas alinhadas numa lista longa. */}
                                        <div style={{ width: 44, flexShrink: 0, textAlign: 'center' }}>
                                            <div style={{
                                                fontSize: 18, fontWeight: 800, lineHeight: 1.1,
                                                fontVariantNumeric: 'tabular-nums',
                                                color: ehHoje ? COR : '#334155',
                                            }}>
                                                {dd}
                                            </div>
                                            <div style={{ fontSize: 10, color: ehHoje ? COR : '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                                                {ehHoje ? 'hoje' : diaSemana}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            {linhas.map((t) => (
                                                <div key={t.idTecnico} style={{ marginBottom: 6 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                                        <span style={{
                                                            width: 8, height: 8, borderRadius: '50%',
                                                            backgroundColor: corDoTecnico(t.idTecnico), flexShrink: 0,
                                                        }} />
                                                        {/* ⚠️ No celular o nome do técnico é ESCRITO. Na grade
                                                            ele é só a cor da bolinha, decifrada pela legenda —
                                                            que numa lista rolada fica fora da tela. */}
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
                                                            {t.tecnico}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 8px', paddingLeft: 14 }}>
                                                        {t.lojas.map((l) => (
                                                            // ⚠️ NO CELULAR O TOQUE É O ÚNICO CAMINHO — não existe
                                                            //    `title` num aparelho sem mouse. Por isso a loja virou
                                                            //    botão com alvo de 32px: era aqui que a contagem
                                                            //    ficava sem nenhuma forma de virar detalhe.
                                                            <button key={l.loja} type="button"
                                                                onClick={(e) => alternarDetalhe(e, cel.chave, t, l)}
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                    minHeight: 32, padding: '4px 10px', borderRadius: 8,
                                                                    border: '1px solid #e2e8f0', backgroundColor: '#fff',
                                                                    fontSize: 12, color: '#334155', cursor: 'pointer',
                                                                    fontFamily: 'inherit',
                                                                }}>
                                                                <strong>{l.loja}</strong>
                                                                {l.chamados > 1 && (
                                                                    <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                                                                        ×{l.chamados}
                                                                    </span>
                                                                )}
                                                                {l.atrasados > 0 && <AlertTriangle size={11} color="#dc2626" />}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                    ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: 700 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                                {DIAS_SEMANA.map((d) => (
                                    <div key={d} style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textAlign: 'center', textTransform: 'uppercase' }}>
                                        {d}
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                                {celulas.map((cel) => {
                                    const linhas = agenda.dias?.[cel.chave] || [];
                                    const ehHoje = cel.chave === hojeChave;
                                    return (
                                        <div key={cel.chave} style={{
                                            minHeight: 92, padding: 6, borderRadius: 8,
                                            border: ehHoje ? `2px solid ${COR}` : '1px solid #f1f5f9',
                                            backgroundColor: cel.doMes ? '#fff' : '#fafafa',
                                            opacity: cel.doMes ? 1 : 0.5,
                                        }}>
                                            <div style={{
                                                fontSize: 12, fontWeight: ehHoje ? 800 : 600,
                                                color: ehHoje ? COR : '#64748b', marginBottom: 4,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>
                                                {cel.dia}
                                            </div>
                                            {linhas.map((t) => (
                                                <div key={t.idTecnico} style={{ marginBottom: 4 }}>
                                                    {t.lojas.map((l) => (
                                                        // ⚠️ É um `button`, não uma `div` com `onClick`: dá foco pelo
                                                        //    teclado e Enter/Espaço de graça. O `title` continua para
                                                        //    quem só passa o mouse — a pop-up é o detalhe de quem clica.
                                                        <button key={l.loja} type="button"
                                                            onClick={(e) => alternarDetalhe(e, cel.chave, t, l)}
                                                            title={`${t.tecnico} — ${l.loja} · clique para ver os assuntos`}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 4, width: '100%',
                                                                fontSize: 11, lineHeight: 1.5, color: '#334155',
                                                                background: 'none', border: 'none', padding: 0,
                                                                textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                                                            }}>
                                                            <span style={{
                                                                width: 6, height: 6, borderRadius: '50%',
                                                                backgroundColor: corDoTecnico(t.idTecnico), flexShrink: 0,
                                                            }} />
                                                            <strong>{l.loja}</strong>
                                                            {/* A contagem só aparece quando há mais de um — "FER 1"
                                                                é ruído, "FER 3" é informação. */}
                                                            {l.chamados > 1 && (
                                                                <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                                                                    ×{l.chamados}
                                                                </span>
                                                            )}
                                                            {l.atrasados > 0 && (
                                                                <AlertTriangle size={11} color="#dc2626" title={`${l.atrasados} atrasado(s)`} />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* ⚠️ O bloco "a agendar" é o que impede o calendário de mentir
                        por omissão. Chamado atribuído sem data não aparece em dia
                        nenhum — sem isto, um mês vazio se leria como "nada a fazer". */}
                    <div style={{
                        marginTop: 18, padding: 14, borderRadius: 10,
                        backgroundColor: agenda.semData?.length ? '#fffbeb' : '#f8fafc',
                        border: `1px solid ${agenda.semData?.length ? '#fde68a' : '#f1f5f9'}`,
                    }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: agenda.semData?.length ? 8 : 0 }}>
                            <CalendarClock size={16} color={agenda.semData?.length ? '#b45309' : '#94a3b8'} />
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: agenda.semData?.length ? '#92400e' : '#64748b' }}>
                                {agenda.semData?.length
                                    ? `${agenda.semData.length} chamado(s) atribuído(s) e sem data`
                                    : 'Nenhum chamado atribuído esperando agendamento'}
                            </p>
                        </div>
                        {!!agenda.semData?.length && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {agenda.semData.map((c) => (
                                    <span key={c.ID} style={{
                                        fontSize: 12, padding: '4px 10px', borderRadius: 6,
                                        backgroundColor: '#fff', border: '1px solid #fde68a', color: '#334155',
                                    }}>
                                        <strong>#{c.ID}</strong> {c.LOJA} · {c.TECNICO_NOME || 'sem técnico'}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Anomalia: agendado sem técnico. Não deveria existir (a triagem
                        grava os dois juntos), mas escondê-la é o que impediria alguém
                        de consertar. */}
                    {!!agenda.semTecnico?.length && (
                        <p style={{ fontSize: 12, color: '#b91c1c', margin: '10px 0 0 0' }}>
                            ⚠️ {agenda.semTecnico.length} chamado(s) com data e <strong>sem técnico</strong> —
                            faça a triagem: {agenda.semTecnico.map((c) => `#${c.ID}`).join(', ')}
                        </p>
                    )}

                    <p style={{ fontSize: 12, color: '#94a3b8', margin: '14px 0 0 0', lineHeight: 1.5 }}>
                        Cada ponto é <strong>um técnico numa loja</strong> naquele dia; <code>×N</code> é o
                        número de chamados dele ali — <strong>clique na loja</strong> para ver os assuntos.
                        O triângulo vermelho marca visita cuja data já passou e o atendimento não foi
                        concluído.
                    </p>
                </>
            )}

            {/* ==================================================================
                A POP-UP DOS ASSUNTOS.

                ⚠️⚠️ Ela é o DETALHE de uma célula agrupada, não uma segunda
                   leitura do calendário: mostra os chamados de UM técnico em UMA
                   loja em UM dia. É o que a célula esconde ao dizer só "×3".

                ⚠️ Fica FORA do bloco `!carregando && agenda`, no fim do
                   componente: por ser `fixed`, ela não pertence a lugar nenhum da
                   árvore visual, e deixá-la aqui evita que um recarregamento do
                   mês a desmonte no meio da leitura.
                ================================================================== */}
            {detalhe && (
                <>
                    {/* O véu invisível que fecha ao clicar fora. ⚠️ Invisível de
                        propósito (sem escurecer a tela): isto é uma pop-up de
                        detalhe, não um modal — escurecer o calendário atrás dela
                        esconderia justamente o contexto que se está lendo. */}
                    <div
                        onClick={() => setDetalhe(null)}
                        style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'transparent' }}
                    />
                    <div
                        role="dialog"
                        aria-label={`Chamados de ${detalhe.tecnico} na loja ${detalhe.loja}`}
                        style={{
                            position: 'fixed', ...detalhe.pos, zIndex: 1001,
                            width: LARGURA_POPUP, maxWidth: 'calc(100vw - 16px)',
                            backgroundColor: '#fff', borderRadius: 10,
                            border: `1px solid ${COR_CLARA}`,
                            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
                            overflow: 'hidden',
                        }}
                    >
                        <div style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                            padding: '10px 12px', borderBottom: '1px solid #f1f5f9',
                            backgroundColor: '#fbf7ff',
                        }}>
                            <span style={{
                                width: 8, height: 8, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                                backgroundColor: corDoTecnico(detalhe.idTecnico),
                            }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                    margin: 0, fontSize: 13, fontWeight: 800, color: '#0f172a',
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {detalhe.loja} · {diaCurto(detalhe.dia)}
                                </p>
                                {/* O nome do técnico ESCRITO: na grade ele é só a cor
                                    da bolinha, e a pop-up tapa a legenda. */}
                                <p style={{ margin: '2px 0 0 0', fontSize: 11, color: '#64748b' }}>
                                    {detalhe.tecnico}
                                </p>
                            </div>
                            <button type="button" aria-label="Fechar" onClick={() => setDetalhe(null)}
                                style={{
                                    display: 'flex', padding: 4, borderRadius: 6, border: 'none',
                                    background: 'none', cursor: 'pointer', color: '#94a3b8',
                                }}>
                                <X size={15} />
                            </button>
                        </div>

                        {/* ⚠️ `maxHeight` + rolagem: um técnico com 8 chamados na
                            mesma loja no mesmo dia faria a pop-up passar da tela. */}
                        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                            {!detalhe.itens.length ? (
                                // Não deveria acontecer (a linha só existe porque há
                                // chamado), mas frase é melhor que caixa vazia.
                                <p style={{ margin: 0, padding: '12px', fontSize: 12, color: '#94a3b8' }}>
                                    Sem detalhe dos chamados — recarregue a página.
                                </p>
                            ) : detalhe.itens.map((i) => (
                                <div key={i.id} style={{
                                    padding: '9px 12px', borderBottom: '1px solid #f8fafc',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 800, color: COR,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}>
                                            #{i.id}
                                        </span>
                                        <span style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                            {i.tipo || 'sem tipo'}
                                        </span>
                                        {/* ⚠️ O atraso é POR CHAMADO: o triângulo da célula
                                            diz "há atraso aqui", e é esta marca que diz
                                            QUAL dos chamados está atrasado. */}
                                        {i.atrasado && (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                                fontSize: 10, fontWeight: 700, color: '#b91c1c',
                                            }}>
                                                <AlertTriangle size={10} /> atrasado
                                            </span>
                                        )}
                                    </div>
                                    {/* O ASSUNTO — é ele que a pessoa vem buscar aqui. */}
                                    <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#334155', lineHeight: 1.4 }}>
                                        {i.titulo || '(sem assunto)'}
                                    </p>
                                    <p style={{ margin: '2px 0 0 0', fontSize: 11, color: '#64748b' }}>
                                        {rotuloStatus(i.status)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ChamadosCalendario;
